import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth-context";
import AlchamisLogo from "@/components/AlchamisLogo";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate("/admin");
    } else {
      toast.error(res.error || "Login failed");
    }
  }

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex bg-[#060A14]">
      <Seo title="Internal Portal · Sign in" noindex />
      <div className="hidden lg:flex w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1611416457332-946853cc75d6?auto=format&fit=crop&w=1800&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#000B24] via-[#001B54]/80 to-[#000B24]" />
        <div className="relative w-full p-16 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3 text-white" data-testid="admin-back-home">
            <ArrowLeft size={14} /> <span className="text-xs tracking-[0.22em] uppercase">Public site</span>
          </Link>
          <div>
            <AlchamisLogo variant="full" height={88} tagline="Internal Portal" />
            <div className="font-display text-5xl text-white mt-8 leading-tight">
              The AlchamisGroup
              <span className="block italic gold-text">internal portal.</span>
            </div>
            <p className="text-white/65 max-w-md mt-4">A private workspace for partners and engagement leads.</p>
          </div>
          <div className="text-xs tracking-[0.32em] uppercase text-white/30">Restricted Access · By Invitation Only</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 md:px-16 py-20">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="admin-login-form">
          <div>
            <div className="overline">Sign-in</div>
            <h1 className="font-display text-4xl text-white mt-3">Welcome back.</h1>
            <p className="text-white/55 text-sm mt-2">Use your AlchamisGroup credentials.</p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[0.65rem] tracking-[0.22em] uppercase text-white/50">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full mt-2 bg-[#0A0F1F] border border-white/10 focus:border-gold/60 outline-none px-4 py-3.5 text-sm text-white" />
            </div>
            <div>
              <label className="text-[0.65rem] tracking-[0.22em] uppercase text-white/50">Password</label>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full mt-2 bg-[#0A0F1F] border border-white/10 focus:border-gold/60 outline-none px-4 py-3.5 text-sm text-white" />
            </div>
          </div>

          <button type="submit" disabled={submitting} className="luxury-button w-full justify-center" data-testid="admin-login-submit">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Signing in</> : <>Sign in <ArrowRight size={14} /></>}
          </button>

          <div className="text-[0.65rem] text-white/40 tracking-wider mt-6 text-center">
            Sessions are end-to-end encrypted. Access is logged.
          </div>
        </form>
      </div>
    </div>
  );
}
